/**
 * Word clouds: counting the words of a text, then packing them into a rectangle.
 *
 * Counting is ordinary word frequency — split into words, fold capitals if asked, drop
 * the stopwords, tally. Placement is the spiral method d3-cloud popularised: each word,
 * largest first, starts at the centre and walks outward along an Archimedean spiral
 * until its box touches nothing already placed. A seeded random source picks each
 * word's starting angle and direction, so a seed always gives the same cloud and "new
 * layout" is just the next seed.
 *
 * The layout never measures text itself. The widget measures each word with a canvas
 * after mount (the browser knows the real font) and the tests pass an estimate; both go
 * through `MeasureWord`, so the arithmetic is the same either way.
 */

import type { Rect } from "@/lib/tool-kit/charts/layout";
import { mixHex } from "@/lib/tool-kit/diagrams/color";
import { RectIndex, seededRandom } from "@/lib/tool-kit/diagrams/geometry";
import { CHART_COLORS, CHART_INK } from "@/lib/tool-kit/palette";

/* ------------------------------------------------------------- counting */

const WORD = /[\p{L}\p{M}\p{N}]+(?:['’-][\p{L}\p{M}\p{N}]+)*/gu;
const HAS_LETTER = /\p{L}/u;
const APOSTROPHES = /['’]/g;
const CURLY_APOSTROPHE = /’/g;
const STOPWORD_SEPARATORS = /[\s,;、，]+/u;
/** Scripts written without spaces between words; these need a word segmenter. */
const UNSPACED_SCRIPT =
  /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}\p{Script=Lao}\p{Script=Khmer}\p{Script=Myanmar}]/u;

/** The words of `text`, in order. In-word apostrophes and hyphens are kept. */
export function tokenize(text: string, locale = "en"): string[] {
  if (UNSPACED_SCRIPT.test(text) && typeof Intl.Segmenter === "function") {
    const segmenter = new Intl.Segmenter(locale, { granularity: "word" });
    return [...segmenter.segment(text)]
      .filter((part) => part.isWordLike)
      .map((part) => part.segment);
  }
  return text.match(WORD) ?? [];
}

/** How a word is compared with the stopword list: lower case, apostrophes removed. */
export function stopwordKey(word: string, locale = "en"): string {
  return word.toLocaleLowerCase(locale).replace(APOSTROPHES, "");
}

/** A stopword list typed as words separated by spaces, commas or new lines. */
export function parseStopwords(text: string, locale = "en"): Set<string> {
  return new Set(
    text
      .split(STOPWORD_SEPARATORS)
      .filter(Boolean)
      .map((word) => stopwordKey(word, locale))
  );
}

export interface WordFrequency {
  /** How the word is drawn: its most frequent spelling in the text. */
  text: string;
  count: number;
  /** Position of its first occurrence — the tie-breaker between equal counts. */
  first: number;
}

export interface FrequencyOptions {
  stopwords: ReadonlySet<string>;
  removeStopwords: boolean;
  /** Count "Nation" and "nation" as one word. */
  foldCase: boolean;
  /** Count tokens with no letters in them, such as years. */
  includeNumbers: boolean;
  locale?: string;
}

export interface FrequencyResult {
  /** Most frequent first; ties in order of first appearance. */
  words: WordFrequency[];
  /** Every word in the text. */
  tokens: number;
  /** Words left after removing stopwords (and numbers, unless included). */
  counted: number;
}

interface Tally {
  count: number;
  first: number;
  spellings: Map<string, number>;
}

function mostFrequentSpelling(spellings: Map<string, number>): string {
  let best = "";
  let bestCount = 0;
  // Map iteration follows insertion order, so a tie keeps the earlier spelling.
  for (const [spelling, count] of spellings) {
    if (count > bestCount) {
      best = spelling;
      bestCount = count;
    }
  }
  return best;
}

/** Count the words of `text` for a word cloud or a frequency table. */
export function wordFrequencies(
  text: string,
  options: FrequencyOptions
): FrequencyResult {
  const locale = options.locale ?? "en";
  const tokens = tokenize(text, locale);
  const tallies = new Map<string, Tally>();
  let counted = 0;
  for (const [index, raw] of tokens.entries()) {
    const word = raw.replace(CURLY_APOSTROPHE, "'");
    if (!(options.includeNumbers || HAS_LETTER.test(word))) {
      continue;
    }
    if (
      options.removeStopwords &&
      options.stopwords.has(stopwordKey(word, locale))
    ) {
      continue;
    }
    counted += 1;
    const key = options.foldCase ? word.toLocaleLowerCase(locale) : word;
    const tally = tallies.get(key) ?? {
      count: 0,
      first: index,
      spellings: new Map<string, number>(),
    };
    tally.count += 1;
    tally.spellings.set(word, (tally.spellings.get(word) ?? 0) + 1);
    tallies.set(key, tally);
  }
  const words = [...tallies.values()]
    .map((tally) => ({
      text: mostFrequentSpelling(tally.spellings),
      count: tally.count,
      first: tally.first,
    }))
    .sort((a, b) => b.count - a.count || a.first - b.first);
  return { words, tokens: tokens.length, counted };
}

/** The words a cloud draws: at least `minCount` occurrences, at most `maxWords` words. */
export function selectWords(
  words: readonly WordFrequency[],
  minCount: number,
  maxWords: number
): WordFrequency[] {
  return words
    .filter((word) => word.count >= minCount)
    .slice(0, Math.max(0, maxWords));
}

/* --------------------------------------------------------------- sizing */

export type SizeScale = "linear" | "sqrt";

/**
 * Font size for a word used `count` times when the commonest is used `maxCount` times.
 *
 * Linear: height in proportion to the count — a word used half as often is half as
 * tall, and so covers about a quarter of the area. Square root: height in proportion to
 * the square root, so a word's area (for words of equal length) is in proportion to its
 * count. Either way no word is drawn smaller than `minSize`.
 */
export function wordSize(
  count: number,
  maxCount: number,
  scale: SizeScale,
  maxSize: number,
  minSize: number
): number {
  const ratio = maxCount > 0 ? count / maxCount : 1;
  const factor = scale === "linear" ? ratio : Math.sqrt(ratio);
  return Math.max(minSize, maxSize * factor);
}

/**
 * Which words are drawn vertically: none, or about a third of them (never the first,
 * largest word), chosen by the seed so a layout can be reproduced.
 */
export function wordOrientations(
  count: number,
  vertical: boolean,
  seed: number
): boolean[] {
  const random = seededRandom(seed * 7919 + 17);
  return Array.from({ length: count }, (_, index) => {
    const draw = random();
    return vertical && index > 0 && draw < 0.34;
  });
}

/* ---------------------------------------------------------------- layout */

/** A word's ink box at a given size: advance width, and height above and below the baseline. */
export interface WordMetrics {
  width: number;
  ascent: number;
  descent: number;
}

export type MeasureWord = (text: string, fontSize: number) => WordMetrics;

export interface CloudWord {
  text: string;
  size: number;
  vertical: boolean;
}

export interface PlacedWord {
  /** Index into the words passed in. */
  index: number;
  text: string;
  size: number;
  vertical: boolean;
  /** Centre of the word's box. */
  x: number;
  y: number;
  /** Distance from the box centre to the baseline, along the text's own vertical. */
  baseline: number;
  box: Rect;
}

export interface CloudLayout {
  words: PlacedWord[];
  /** Indices of words that found no room. */
  dropped: number[];
  /** The factor every size was multiplied by to make the words fit. */
  scale: number;
}

export interface CloudOptions {
  seed: number;
  /** Space kept between words. */
  padding?: number;
}

const SPIRAL_STEP = 0.1;
const SHRINK = 0.9;
const MAX_ATTEMPTS = 10;

function inside(box: Rect, area: Rect): boolean {
  return (
    box.x >= area.x &&
    box.y >= area.y &&
    box.x + box.width <= area.x + area.width &&
    box.y + box.height <= area.y + area.height
  );
}

interface Spiral {
  start: number;
  direction: number;
}

/** Walk the spiral until the word's box fits, or give up past the area's corners. */
function findSpot(
  size: { width: number; height: number },
  area: Rect,
  index: RectIndex,
  spiral: Spiral,
  padding: number
): Rect | null {
  const cx = area.x + area.width / 2;
  const cy = area.y + area.height / 2;
  const stretch = area.width / area.height;
  const reach = Math.hypot(area.width, area.height) / 2;
  for (let step = 0; ; step++) {
    const t = step * SPIRAL_STEP;
    const radius = t;
    if (radius > reach) {
      return null;
    }
    const angle = spiral.start + spiral.direction * t;
    const x = cx + stretch * radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    const box = {
      x: x - size.width / 2,
      y: y - size.height / 2,
      width: size.width,
      height: size.height,
    };
    if (inside(box, area) && !index.collides(box, padding)) {
      return box;
    }
  }
}

function placeAll(
  words: readonly CloudWord[],
  area: Rect,
  measure: MeasureWord,
  scale: number,
  options: CloudOptions
): CloudLayout {
  const random = seededRandom(options.seed);
  const padding = options.padding ?? 3;
  const index = new RectIndex(24);
  const placed: PlacedWord[] = [];
  const dropped: number[] = [];
  for (const [position, word] of words.entries()) {
    const spiral = {
      start: random() * 2 * Math.PI,
      direction: random() < 0.5 ? 1 : -1,
    };
    const size = word.size * scale;
    const metrics = measure(word.text, size);
    const tall = metrics.ascent + metrics.descent;
    const box = findSpot(
      word.vertical
        ? { width: tall, height: metrics.width }
        : { width: metrics.width, height: tall },
      area,
      index,
      spiral,
      padding
    );
    if (!box) {
      dropped.push(position);
      continue;
    }
    index.add(box);
    placed.push({
      index: position,
      text: word.text,
      size,
      vertical: word.vertical,
      x: box.x + box.width / 2,
      y: box.y + box.height / 2,
      baseline: (metrics.ascent - metrics.descent) / 2,
      box,
    });
  }
  return { words: placed, dropped, scale };
}

/**
 * Pack `words` (largest first) into `area` without overlaps. When some word finds no
 * room, every size is shrunk by 10% and the packing starts again — up to ten times — so
 * the whole list is shown smaller rather than the tail silently dropped. If words still
 * do not fit, the layout with the fewest left out is returned and they are listed in
 * `dropped`.
 */
export function layoutCloud(
  words: readonly CloudWord[],
  area: Rect,
  measure: MeasureWord,
  options: CloudOptions
): CloudLayout {
  let best: CloudLayout | null = null;
  let scale = 1;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    const layout = placeAll(words, area, measure, scale, options);
    if (layout.dropped.length === 0) {
      return layout;
    }
    if (!best || layout.dropped.length < best.dropped.length) {
      best = layout;
    }
    scale *= SHRINK;
  }
  return best ?? { words: [], dropped: [], scale: 1 };
}

/* ---------------------------------------------------------------- colours */

export type CloudPalette = "colorblind" | "blue" | "gray";

/**
 * Okabe–Ito colours that keep at least 3:1 contrast with white, the bar for large text:
 * blue, vermillion, bluish green and reddish purple, then ink. Orange, sky blue and
 * yellow are too pale for words on white.
 */
const TEXT_SAFE = [
  CHART_COLORS[0],
  CHART_COLORS[3],
  CHART_COLORS[2],
  CHART_COLORS[5],
  CHART_INK.text,
];

/**
 * A colour for each word, most frequent first. The colour-blind-safe palette cycles by
 * rank; the single-hue palettes darken the most frequent words, so colour repeats what
 * size already says instead of adding a second, unexplained code.
 */
export function cloudColors(count: number, palette: CloudPalette): string[] {
  return Array.from({ length: count }, (_, rank) => {
    if (palette === "colorblind") {
      return TEXT_SAFE[rank % TEXT_SAFE.length];
    }
    const top = rank < Math.max(1, Math.ceil(count * 0.15));
    if (palette === "gray") {
      return top ? CHART_INK.text : CHART_INK.muted;
    }
    return top
      ? mixHex(CHART_COLORS[0], CHART_INK.text, 0.45)
      : CHART_COLORS[0];
  });
}
